// ── Global "Tell Me" search index ──────────────────────────────────────────
// The BC "Tell Me what you want to do" (Alt+Q) equivalent: a flat, searchable
// list of every navigable page / list / report in the app, built from the SAME
// navigation config the menus already use (config/moduleNav.js → TOP_MODULES),
// so the search stays in lock-step with the menus with zero duplication.
//
// Results are permission-filtered per user with the exact rule the router
// enforces in App.js PrivateRoute: admin / super-admin see everything; a class
// teacher sees only their scoped pages; every other staff user sees only pages
// whose owning module their PermissionSets grant (via canView()). So "admin can
// search anything, everyone else only what they're allowed to see" falls out of
// the permission layer we already have — no separate access list to maintain.
import { TOP_MODULES } from './moduleNav';
import { canView, moduleForPath } from '../utils/permissions';

// Extra destinations that aren't part of a module's side-menu but are useful to
// jump to. module=null ⇒ logged-in-only (no module permission needed), matching
// moduleForPath() returning null for these routes.
const EXTRA_DESTINATIONS = [
  { label: 'Main Dashboard', path: '/dashboard',            moduleTitle: 'Home',      group: 'General', keywords: 'home role center start' },
  { label: 'My Approvals',   path: '/approvals',            moduleTitle: 'Approvals', group: 'General', keywords: 'inbox approve pending workflow' },
  { label: 'Messages',       path: '/messages',             moduleTitle: 'Messaging', group: 'General', keywords: 'mail inbox notifications chat' },
  { label: 'Class Teacher Centre', path: '/class-teacher-centre', moduleTitle: 'Academics', group: 'Class Teacher', keywords: 'my class register' },
];

// BC-style synonyms so users find pages by the term they know, not just the
// on-screen label. Keyed by path; folded into the searchable text.
const KEYWORD_ALIASES = {
  '/gl-accounts':               'coa chart of accounts ledger nominal',
  '/finance/income-statement':  'p&l pnl profit and loss p and l',
  '/finance/balance-sheet':     'bs financial position',
  '/finance/trial-balance':     'tb',
  '/finance/balances':          'debtors outstanding arrears',
  '/finance/aged-receivables':  'aging ageing overdue',
  '/learners':                  'students customers pupils',
  '/suppliers':                 'vendors creditors',
  '/payables/aging':            'aged payables aging ageing',
  '/finance/vat-return':        'tax vat',
  '/academics/merit-list':      'ranking positions results',
  '/academics/score-entry':     'marks grades enter scores',
  '/hr/employees':              'staff personnel',
  '/payroll/runs':              'salary wages pay',
  '/setups/users':              'accounts logins security',
  '/setups/permission-sets':    'roles rights access security rbac',
};

// Class-teacher reachable pages — mirrors App.js PrivateRoute exactly.
const classTeacherCanReach = (p) =>
  p === '/class-teacher-centre' || p === '/dashboard' ||
  p.startsWith('/academics/assessment-setup') ||
  p.startsWith('/academics/score-entry') ||
  p.startsWith('/academics/assessment-report') ||
  p.startsWith('/academics/merit-list') ||
  p.startsWith('/academics/lernalytics') ||
  p.startsWith('/academics/events');

// Can this user actually open this path? Same gate the router applies, so the
// palette never lists a page that would just bounce the user to /dashboard.
const canReach = (path, user) => {
  if (user.isSuperAdmin || user.role === 'admin') return true;
  if (user.role === 'classTeacher') return classTeacherCanReach(path);
  const mod = moduleForPath(path);      // authoritative path → module mapping
  if (!mod) return true;                 // logged-in-only routes (dashboard, etc.)
  return canView(mod);
};

// Build the full, deduped, permission-filtered index for the given user.
// Paths can appear under more than one module (e.g. Charge Items shows in both
// Academics and Finance menus); we keep one entry per path and fold the other
// module/group names in as extra searchable tags so either term still finds it.
export const buildSearchIndex = (user) => {
  if (!user || user.role === 'parent') return []; // parents use the portal nav
  const byPath = new Map();

  const add = (label, path, moduleTitle, group, keywords = '') => {
    if (!path) return;
    const existing = byPath.get(path);
    if (existing) {
      // Same destination reached from another module — enrich its tags.
      existing.tags = `${existing.tags} ${moduleTitle} ${group} ${keywords}`.trim();
      return;
    }
    byPath.set(path, {
      label, path, moduleTitle, group,
      tags: `${keywords} ${KEYWORD_ALIASES[path] || ''}`.trim(),
    });
  };

  TOP_MODULES.forEach(mod => {
    (mod.groups || []).forEach(grp => {
      (grp.items || []).forEach(it => add(it.label, it.path, mod.title, grp.title));
    });
  });
  EXTRA_DESTINATIONS.forEach(d => add(d.label, d.path, d.moduleTitle, d.group, d.keywords));

  return [...byPath.values()].filter(e => canReach(e.path, user));
};

// Rank entries against a query. Returns a sorted, sliced list. AND-matches all
// query tokens across label + module + group + tags; ranks exact/prefix/word
// matches on the visible label above generic substring hits.
export const searchEntries = (index, query, limit = 30) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);

  const scored = [];
  for (const e of index) {
    const label = e.label.toLowerCase();
    const hay = `${label} ${e.moduleTitle} ${e.group} ${e.tags}`.toLowerCase();
    if (!tokens.every(t => hay.includes(t))) continue;

    let score = 0;
    if (label === q) score += 100;
    else if (label.startsWith(q)) score += 60;
    else if (new RegExp(`\\b${escapeRegExp(q)}`).test(label)) score += 40;
    else if (label.includes(q)) score += 25;
    else score += 10; // matched only via module/group/tags
    scored.push({ ...e, score });
  }
  scored.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  return scored.slice(0, limit);
};

function escapeRegExp(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
